import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { workoutAPI } from '../services/workoutAPI';

function ExerciseForm({ show, onHide, exercise, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    name: '',
    exercise_type: 'weight_reps',
    category: [],
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [hasLogs, setHasLogs] = useState(false);
  const [checkingLogs, setCheckingLogs] = useState(false);

  useEffect(() => {
    if (exercise) {
      // Convert category string to array if it exists
      const categoryArray = exercise.category
        ? exercise.category.split(',').map(c => c.trim()).filter(Boolean)
        : [];

      setFormData({
        name: exercise.name || '',
        exercise_type: exercise.exercise_type || 'weight_reps',
        category: categoryArray,
        notes: exercise.notes || ''
      });

      // Check if exercise has logs (for edit mode only)
      setCheckingLogs(true);
      workoutAPI.checkExerciseHasLogs(exercise.id)
        .then(result => {
          setHasLogs(result.hasLogs);
        })
        .catch(err => {
          console.error('Failed to check exercise logs:', err);
          // Default to false - allow editing if check fails
          setHasLogs(false);
        })
        .finally(() => {
          setCheckingLogs(false);
        });
    } else {
      setFormData({
        name: '',
        exercise_type: 'weight_reps',
        category: [],
        notes: ''
      });
      setHasLogs(false);
      setCheckingLogs(false);
    }
    setErrors({});
  }, [exercise, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleExerciseTypeChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      exercise_type: selectedOption ? selectedOption.value : 'weight_reps'
    }));
    // Clear error for this field
    if (errors.exercise_type) {
      setErrors(prev => ({
        ...prev,
        exercise_type: ''
      }));
    }
  };

  const handleCategoryChange = (selectedOptions) => {
    const categories = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setFormData(prev => ({
      ...prev,
      category: categories
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Exercise name is required';
    }

    if (!formData.exercise_type) {
      newErrors.exercise_type = 'Exercise type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Convert category array to comma-separated string for saving
    const dataToSave = {
      ...formData,
      category: formData.category.length > 0 ? formData.category.join(', ') : ''
    };

    onSave(dataToSave);
  };

  const exerciseTypeOptions = [
    { value: 'weight_reps', label: 'Weight & Reps - E.g., Bench Press, Squats' },
    { value: 'weight_time', label: 'Weight & Time - E.g., Plank Hold with Weight' },
    { value: 'cardio_basic', label: 'Cardio Basic - Distance, Time, Heart Rate' },
    { value: 'cardio_segments', label: 'Cardio Intervals - Multiple segments with distance/time' }
  ];

  const categoryOptions = [
    { value: 'Chest', label: 'Chest' },
    { value: 'Back', label: 'Back' },
    { value: 'Shoulders', label: 'Shoulders' },
    { value: 'Arms', label: 'Arms' },
    { value: 'Legs', label: 'Legs' },
    { value: 'Core', label: 'Core' },
    { value: 'Cardio', label: 'Cardio' },
    { value: 'Full Body', label: 'Full Body' },
    { value: 'Other', label: 'Other' }
  ];

  // Get the selected value for exercise type
  const selectedExerciseType = exerciseTypeOptions.find(
    option => option.value === formData.exercise_type
  );

  // Get the selected values for category
  const selectedCategories = categoryOptions.filter(
    option => formData.category.includes(option.value)
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{exercise ? 'Edit Exercise' : 'Create New Exercise'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Exercise Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              isInvalid={!!errors.name}
              placeholder="e.g., Bench Press, Running, Squats"
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Exercise Type *
              {hasLogs && (
                <Badge bg="warning" text="dark" className="ms-2">
                  🔒 Locked
                </Badge>
              )}
            </Form.Label>
            <Select
              options={exerciseTypeOptions}
              value={selectedExerciseType}
              onChange={handleExerciseTypeChange}
              placeholder="Select exercise type..."
              isSearchable={true}
              isClearable={false}
              isDisabled={hasLogs || checkingLogs}
              className={errors.exercise_type ? 'is-invalid' : ''}
            />
            {errors.exercise_type && (
              <div className="invalid-feedback d-block">
                {errors.exercise_type}
              </div>
            )}
            {hasLogs ? (
              <Form.Text className="text-warning">
                ⓘ Exercise type cannot be changed because this exercise has workout logs.
                This prevents data corruption.
              </Form.Text>
            ) : (
              <Form.Text className="text-muted">
                Select how you'll track this exercise
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Select
              isMulti
              options={categoryOptions}
              value={selectedCategories}
              onChange={handleCategoryChange}
              placeholder="Select categories..."
              isSearchable={true}
              isClearable={true}
              closeMenuOnSelect={false}
            />
            <Form.Text className="text-muted">
              Optional: select one or more categories to help organize your exercises
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Optional notes about form, cues, etc."
            />
          </Form.Group>

          <div className="alert alert-info">
            <strong>Exercise Type Info:</strong>
            <ul className="mb-0 mt-2">
              <li><strong>Weight & Reps:</strong> Track weight (lbs/kg) and repetitions per set</li>
              <li><strong>Weight & Time:</strong> Track weight and duration (e.g., weighted planks)</li>
              <li><strong>Cardio Basic:</strong> Track distance, time, and optional heart rate</li>
              <li><strong>Cardio Intervals:</strong> Track multiple segments with individual times/distances</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : (exercise ? 'Update Exercise' : 'Create Exercise')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ExerciseForm;
